import React, { useMemo } from "react";
import { format, differenceInDays } from "date-fns";
import { FaArrowUp, FaCalendarAlt, FaCircle } from "react-icons/fa";
import CurrencyDisplay from "../Currency/CurrencyDisplay";

export const InvestmentCard = ({ investment }) => {
  // --- Helpers & Logic ---

  // Calculate ROI percentage safely
  const roiPercentage = useMemo(() => {
    if (!investment.amount || !investment.expectedReturn) return 0;
    return (
      ((investment.expectedReturn - investment.amount) / investment.amount) *
      100
    ).toFixed(2);
  }, [investment.amount, investment.expectedReturn]);

  // Calculate Progress (Time Elapsed)
  const progressPercent = useMemo(() => {
    if (!investment.startDate || !investment.endDate) return 0;
    const start = new Date(investment.startDate).getTime();
    const end = new Date(investment.endDate).getTime();
    const now = new Date().getTime();

    if (now > end) return 100;
    if (now < start) return 0;

    const total = end - start;
    const elapsed = now - start;
    return Math.min(100, Math.max(0, (elapsed / total) * 100));
  }, [investment.startDate, investment.endDate]);

  const getStatusStyles = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-emerald-100 text-emerald-700 ring-emerald-600/20";
      case "completed":
        return "bg-primary-100 text-primary-700 ring-primary-600/20";
      case "pending":
        return "bg-amber-100 text-amber-700 ring-amber-600/20";
      default:
        return "bg-gray-100 text-gray-700 ring-gray-600/20";
    }
  };

  return (
    <div className="group relative bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-lg hover:border-primary/30 transition-all duration-300 overflow-hidden">
      {/* Top Decorative Line (Optional branding touch) */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-400 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <div className="p-5">
        {/* --- Header: Plan Name & Status --- */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-gray-900 font-heading tracking-tight">
              {investment.planName || "Investment Plan"}
            </h3>
            <div className="flex items-center text-xs text-gray-500 mt-1 space-x-2">
              <FaCalendarAlt className="text-gray-400" />
              <span>
                {format(
                  new Date(investment.startDate || investment.createdAt),
                  "MMM d, yyyy"
                )}
              </span>
            </div>
          </div>

          <span
            className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ring-1 ring-inset uppercase tracking-wide flex items-center gap-1.5 ${getStatusStyles(
              investment.status
            )}`}
          >
            <FaCircle className="text-[6px]" />
            {investment.status || "Active"}
          </span>
        </div>

        {/* --- Metrics Grid --- */}
        <div className="grid grid-cols-2 gap-y-4 gap-x-8 pb-5 border-b ">
          {/* Principal */}
          <div>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
              Principal
            </p>
            <p className="text-xl font-bold text-gray-900 tabular-nums tracking-tight">
              <CurrencyDisplay
                usdAmount={investment.amount}
                className="inline"
              />
            </p>
          </div>

          {/* Expected Return */}
          <div className="text-right">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
              Total Value
            </p>
            <p
              className={`text-xl font-bold tabular-nums tracking-tight ${
                investment.status === "completed"
                  ? "text-primary"
                  : "text-emerald-600"
              }`}
            >
              <CurrencyDisplay
                usdAmount={investment.expectedReturn}
                className="inline"
              />
            </p>
          </div>
        </div>

        {/* --- Footer: ROI & Timeline --- */}
        <div className="pt-4 flex items-center justify-between">
          {/* ROI Badge */}
          <div className="flex items-center space-x-3">
            <div className="bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg text-sm font-bold flex items-center shadow-sm">
              <FaArrowUp className="text-xs mr-1" />
              {roiPercentage}% ROI
            </div>
            {investment.profitProduction && (
              <span className="text-xs font-medium text-gray-500">
                ({investment.profitProduction}% daily)
              </span>
            )}
          </div>

          {/* Timeline / Progress */}
          <div className="flex flex-col items-end w-1/3">
            <div className="w-full bg-gray-100 rounded-full h-1.5 mb-1.5 overflow-hidden">
              <div
                className="bg-primary h-1.5 rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>
            <span className="text-[10px] font-medium text-gray-400">
              {investment.endDate
                ? `Ends ${format(new Date(investment.endDate), "MMM d")}`
                : "Ongoing"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
